/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React from "react";

import isInputCorrect from "src/utils/ResultValidationHelper";

import Card from "src/components/cards/Card.react";
import ResultDropdown from "src/components/dropdowns/Result.react";

import {
  Table,
  ColHeader,
  TableBody,
  Col,
} from "src/components/tables/Table.react";
import ResultBadge from "src/components/badges/Result.react";

/**
 * The NacelleSubFrame component
 *
 * @author Thais Watanabe
 */
const NacelleSubFrame = (props) => {
  const {
    turbineInspection,
    inspectionModelItems,
    results,
    lastResults,
    lastTurbineInspections,
    openEdit,
    openObs,
    inspection,
  } = props;

  const lastOneYear = lastTurbineInspections.find((obj) =>
    obj.inspection.type.startsWith("1年")
  );

  const i4_8 = inspectionModelItems.filter((obj) => obj.name === "4-8")?.[0];
  const r4_8 = results.filter((obj) => obj.itemId === "4-8")?.[0];
  const last_r4_8 = lastResults?.filter(
    (obj) => obj.itemId === "4-8" && obj.turbineInspectionID === lastOneYear
  )?.[0];

  // Last inspection
  const lastInspection = lastTurbineInspections.find(
    (el) => el.id === last_r4_8?.turbineInspectionID
  );

  return (
    <>
      <Card>
        <Table className="print:mt-2">
          <thead>
            <tr>
              <ColHeader className="text-center" colSpan={19}>
                風向計 値 比較表
              </ColHeader>
            </tr>
            <tr>
              <ColHeader id="4-8" className="w-10 text-center print:hidden" rowSpan={2}>
                <ResultDropdown
                  turbineInspection={turbineInspection}
                  openEdit={openEdit}
                  openObs={openObs}
                  selectedItem={i4_8}
                  selectedResult={r4_8}
                  lastTurbineInspections={lastTurbineInspections}
                  inspection={inspection}
                />
              </ColHeader>
              <ColHeader className="text-center" rowSpan={2}>
                角度
              </ColHeader>
              <ColHeader className="text-center" rowSpan={2}>
                許容範囲
              </ColHeader>
              <ColHeader className="text-center" colSpan={2}>
                今回
              </ColHeader>
              <ColHeader className="text-center" colSpan={2}>
                {lastInspection?.inspection?.name ?? "過去"}
              </ColHeader>
            </tr>
            <tr>
              <ColHeader className="text-center">L側 PC値</ColHeader>
              <ColHeader className="text-center">R側 PC値</ColHeader>
              <ColHeader className="text-center">L側 PC値</ColHeader>
              <ColHeader className="text-center">R側 PC値</ColHeader>
            </tr>
          </thead>
          <TableBody>
            <tr>
              <Col className="text-center print:hidden"></Col>
              <Col className="text-center">0°</Col>
              <Col className="text-center">(330.1°～29.9°)</Col>
              <Col className="text-center">
                <ResultBadge
                  result={r4_8?.childrenResults[0] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[0].unit}
                  isInputCorrect={isInputCorrect("4-8", r4_8?.[0] ?? "-", 0)}
                />
              </Col>
              <Col className="text-center">
                <ResultBadge
                  result={r4_8?.childrenResults[1] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[1].unit}
                  isInputCorrect={isInputCorrect("4-8", r4_8?.[1] ?? "-", 1)}
                />
              </Col>
              <Col className="text-center">
                <ResultBadge
                  result={last_r4_8?.childrenResults[0] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[0].unit}
                  isInputCorrect={isInputCorrect(
                    "4-8",
                    last_r4_8?.[0] ?? "-",
                    0
                  )}
                />
              </Col>
              <Col className="text-center">
                <ResultBadge
                  result={last_r4_8?.childrenResults[1] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[1].unit}
                  isInputCorrect={isInputCorrect(
                    "4-8",
                    last_r4_8?.[1] ?? "-",
                    1
                  )}
                />
              </Col>
            </tr>
            <tr>
              <Col className="text-center print:hidden"></Col>
              <Col className="text-center">90°</Col>
              <Col className="text-center">(60.1°～119.9°)</Col>
              <Col className="text-center">
                <ResultBadge
                  result={r4_8?.childrenResults[2] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[2].unit}
                  isInputCorrect={isInputCorrect("4-8", r4_8?.[2] ?? "-", 2)}
                />
              </Col>
              <Col className="text-center">
                <ResultBadge
                  result={r4_8?.childrenResults[3] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[3].unit}
                  isInputCorrect={isInputCorrect("4-8", r4_8?.[3] ?? "-", 3)}
                />
              </Col>
              <Col className="text-center">
                <ResultBadge
                  result={last_r4_8?.childrenResults[2] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[2].unit}
                  isInputCorrect={isInputCorrect(
                    "4-8",
                    last_r4_8?.[2] ?? "-",
                    2
                  )}
                />
              </Col>
              <Col className="text-center">
                <ResultBadge
                  result={last_r4_8?.childrenResults[3] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[3].unit}
                  isInputCorrect={isInputCorrect(
                    "4-8",
                    last_r4_8?.[3] ?? "-",
                    3
                  )}
                />
              </Col>
            </tr>
            <tr>
              <Col className="text-center print:hidden"></Col>
              <Col className="text-center">180°</Col>
              <Col className="text-center">(150.1°～209.9°)</Col>
              <Col className="text-center">
                <ResultBadge
                  result={r4_8?.childrenResults[4] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[4].unit}
                  isInputCorrect={isInputCorrect("4-8", r4_8?.[4] ?? "-", 4)}
                />
              </Col>
              <Col className="text-center">
                <ResultBadge
                  result={r4_8?.childrenResults[5] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[5].unit}
                  isInputCorrect={isInputCorrect("4-8", r4_8?.[5] ?? "-", 5)}
                />
              </Col>
              <Col className="text-center">
                <ResultBadge
                  result={last_r4_8?.childrenResults[4] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[4].unit}
                  isInputCorrect={isInputCorrect(
                    "4-8",
                    last_r4_8?.[4] ?? "-",
                    4
                  )}
                />
              </Col>
              <Col className="text-center">
                <ResultBadge
                  result={last_r4_8?.childrenResults[5] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[5].unit}
                  isInputCorrect={isInputCorrect(
                    "4-8",
                    last_r4_8?.[5] ?? "-",
                    5
                  )}
                />
              </Col>
            </tr>
            <tr>
              <Col className="text-center print:hidden"></Col>
              <Col className="text-center">270°</Col>
              <Col className="text-center">(240.1°～299.9°)</Col>
              <Col className="text-center">
                <ResultBadge
                  result={r4_8?.childrenResults[6] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[6].unit}
                  isInputCorrect={isInputCorrect("4-8", r4_8?.[6] ?? "-", 6)}
                />
              </Col>
              <Col className="text-center">
                <ResultBadge
                  result={r4_8?.childrenResults[7] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[7].unit}
                  isInputCorrect={isInputCorrect("4-8", r4_8?.[7] ?? "-", 7)}
                />
              </Col>
              <Col className="text-center">
                <ResultBadge
                  result={last_r4_8?.childrenResults[6] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[6].unit}
                  isInputCorrect={isInputCorrect(
                    "4-8",
                    last_r4_8?.[6] ?? "-",
                    6
                  )}
                />
              </Col>
              <Col className="text-center">
                <ResultBadge
                  result={last_r4_8?.childrenResults[7] ?? "-"}
                  unit={i4_8?.inspectionModelItemChildren.items?.[7].unit}
                  isInputCorrect={isInputCorrect(
                    "4-8",
                    last_r4_8?.[7] ?? "-",
                    7
                  )}
                />
              </Col>
            </tr>
          </TableBody>
        </Table>
      </Card>
    </>
  );
};

export default NacelleSubFrame;
